/**
 * "🌬 ลมมรสุม / อากาศ" panel — v0.9.14.
 *
 * Two things, both from sources already in the licence table:
 *  • Which low-level wind regime is over Thailand now + whether a cold air mass is pushing down
 *    (GET /api/monsoon → Open-Meteo wind/temperature/pressure at six regional points).
 *  • The season's El Niño / La Niña context (GET /api/enso → NOAA CPC ONI, public domain).
 *
 * The headline is illustrated by a light inset map: a stylised Thailand outline with one wind arrow
 * per region, each plotted at the region's REAL lat/lon and pointing in the REAL wind direction
 * (length scaled by the real wind speed). A subtle dash flows along each shaft so the motion still
 * reads, but every arrow is data — there is no interpolated/fabricated full-field wind. The country
 * outline itself is an approximate silhouette (a spatial reference, not a scale map), and the caption
 * says so. Every NUMBER shown (wind, temperature, ONI) is real and carries its source + time. This is
 * wind & season context only — never a flood prediction and never an evacuation call.
 */
import { emergencyCard } from "./emergency";

type Regime = "sw-monsoon" | "ne-monsoon" | "mixed" | null;
interface PointWx {
  region: string; name: string; lat: number; lon: number;
  windDir: number | null; windSpeed: number | null; windFromTh: string | null;
  temp: number | null; pressure: number | null; tempMin48: number | null;
}
interface ColdSurge { active: boolean; regions: string[]; dropC: number | null; note: string }
interface MonsoonData {
  regime: Regime; headline: string; detail: string;
  coldSurge: ColdSurge | null; points: PointWx[];
  source: string; updatedAt: string | null; reason?: string;
}
interface EnsoData {
  state: "el-nino" | "la-nina" | "neutral" | null;
  label: string; strength: string | null; oni: number | null; season: string | null;
  trend: { season: string; oni: number }[]; context: string; note: string;
  source: string; updatedAt: string | null; reason?: string;
}

async function loadMonsoon(): Promise<MonsoonData | null> {
  try {
    const r = await fetch("/api/monsoon");
    if (!r.ok) return null;
    const d = (await r.json()) as MonsoonData;
    return d.regime == null && !d.points?.some((p) => p.windDir != null) ? null : d;
  } catch { return null; }
}
async function loadEnso(): Promise<EnsoData | null> {
  try {
    const r = await fetch("/api/enso");
    if (!r.ok) return null;
    const d = (await r.json()) as EnsoData;
    return d.state == null ? null : d;
  } catch { return null; }
}

const esc = (s: string): string => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]!));
const thTime = (iso: string): string =>
  new Date(iso).toLocaleString("th-TH", { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" });
const thDate = (iso: string): string =>
  new Date(iso).toLocaleDateString("th-TH", { day: "numeric", month: "short", year: "numeric" });

/* ── Thailand outline (approximate silhouette — a spatial reference for the inset map, not a scale
 * map). [lon, lat] ring, projected into the canvas with px()/py() below so the real wind arrows land
 * at their true positions over a recognisable country shape. Caption makes clear it is approximate. */
const TH_RING: [number, number][] = [
  [100.1, 20.4], [101.1, 19.6], [101.1, 18.4], [102.1, 18.1], [103.9, 18.3], [104.8, 17.4],
  [105.6, 16.1], [105.0, 14.9], [104.3, 14.4], [102.9, 14.1], [102.6, 12.6], [101.6, 12.7],
  [100.9, 13.5], [100.0, 13.4], [100.2, 12.0], [100.2, 10.5], [99.2, 9.3], [100.1, 8.3],
  [100.3, 7.0], [101.1, 6.3], [99.6, 6.7], [98.4, 7.9], [98.3, 9.5], [98.7, 10.9],
  [99.3, 12.5], [99.1, 13.7], [98.4, 14.9], [97.8, 16.5], [97.4, 18.4], [98.5, 19.8],
];
const LON0 = 97.0, LON1 = 106.0, LAT0 = 5.5, LAT1 = 21.0;

interface WindArrow { x: number; y: number; dx: number; dy: number; len: number; cold: boolean; phase: number }

/** Render the inset wind map: Thailand outline + one real wind arrow per region (position, direction
 * and length are all real data). A subtle dash flows along each shaft in the wind direction. Returns
 * a stop() that cancels the loop. Respects prefers-reduced-motion (renders a single static frame). */
function startWindMap(canvas: HTMLCanvasElement, points: PointWx[], coldRegions: Set<string>): () => void {
  const ctx = canvas.getContext("2d");
  if (!ctx) return () => {};
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const W = canvas.clientWidth || 300, H = canvas.clientHeight || 150;
  canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
  ctx.scale(dpr, dpr);

  const px = (lon: number): number => ((lon - LON0) / (LON1 - LON0)) * W;
  const py = (lat: number): number => ((LAT1 - lat) / (LAT1 - LAT0)) * H;
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // One arrow per region we actually have a wind direction for. windDir is the met. direction the
  // wind comes FROM; the flow (arrow) points toward windDir+180. North is up (screen y grows down).
  const arrows: WindArrow[] = points
    .filter((p) => p.windDir != null && Number.isFinite(p.lat) && Number.isFinite(p.lon))
    .map((p) => {
      const toward = (((p.windDir as number) + 180) % 360) * (Math.PI / 180);
      const len = Math.max(14, Math.min(34, 12 + (p.windSpeed ?? 0) * 0.7));
      return {
        x: px(p.lon), y: py(p.lat),
        dx: Math.sin(toward), dy: -Math.cos(toward), len,
        cold: coldRegions.has(p.name), // coldSurge.regions carries point names, not region keys
        phase: ((px(p.lon) + py(p.lat)) % 60) / 60, // deterministic de-sync, no Math.random
      };
    });

  const drawLand = (): void => {
    ctx.beginPath();
    TH_RING.forEach(([lon, lat], i) => { const x = px(lon), y = py(lat); i ? ctx.lineTo(x, y) : ctx.moveTo(x, y); });
    ctx.closePath();
    ctx.fillStyle = "rgba(0,229,255,0.06)";
    ctx.fill();
    ctx.lineWidth = 1.3; ctx.strokeStyle = "rgba(0,229,255,0.4)"; ctx.stroke();
  };

  const WARM = { shaft: "#39e0c8", head: "#b6fff0", dot: "#eafffb" };
  const COLD = { shaft: "#7fd4ff", head: "#cbeeff", dot: "#eaf7ff" };

  const drawArrow = (a: WindArrow, t: number): void => {
    const c = a.cold ? COLD : WARM;
    const ex = a.x + a.dx * a.len, ey = a.y + a.dy * a.len;
    const ang = Math.atan2(a.dy, a.dx);
    // shaft
    ctx.strokeStyle = c.shaft; ctx.globalAlpha = 0.85; ctx.lineWidth = 2.4; ctx.lineCap = "round";
    ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(ex, ey); ctx.stroke();
    // arrowhead
    ctx.fillStyle = c.head; ctx.globalAlpha = 0.95;
    ctx.beginPath();
    ctx.moveTo(ex, ey);
    ctx.lineTo(ex - 9 * Math.cos(ang - 0.42), ey - 9 * Math.sin(ang - 0.42));
    ctx.lineTo(ex - 9 * Math.cos(ang + 0.42), ey - 9 * Math.sin(ang + 0.42));
    ctx.closePath(); ctx.fill();
    // flowing dash travelling along the shaft in the wind direction
    if (!reduce) {
      const u = (a.phase + t) % 1;
      const hx = a.x + a.dx * a.len * u, hy = a.y + a.dy * a.len * u;
      ctx.strokeStyle = c.head; ctx.globalAlpha = 0.9 * Math.sin(u * Math.PI); ctx.lineWidth = 2.6; ctx.lineCap = "round";
      ctx.beginPath(); ctx.moveTo(hx - a.dx * 6, hy - a.dy * 6); ctx.lineTo(hx, hy); ctx.stroke();
    }
    // dot marking the real measurement location
    ctx.fillStyle = c.dot; ctx.globalAlpha = 1;
    ctx.beginPath(); ctx.arc(a.x, a.y, 2.6, 0, Math.PI * 2); ctx.fill();
    ctx.globalAlpha = 1;
  };

  let raf = 0, t = 0;
  const frame = (): void => {
    ctx.clearRect(0, 0, W, H);
    drawLand();
    for (const a of arrows) drawArrow(a, t);
    if (!reduce) { t = (t + 0.02) % 1; raf = requestAnimationFrame(frame); }
  };
  frame();
  return () => { if (raf) cancelAnimationFrame(raf); };
}

function monsoonSection(d: MonsoonData | null): { html: string; points: PointWx[]; coldRegions: Set<string> } {
  if (!d || d.regime == null) {
    return { html: `<div class="mon-head-row"><span class="mon-head">ลม & มวลอากาศ</span></div>`
      + `<div class="mon-nodata muted">ยังไม่มีข้อมูลลม/อุณหภูมิในขณะนี้ (ดึงจากแหล่งไม่สำเร็จ)</div>`, points: [], coldRegions: new Set() };
  }
  const cold = !!d.coldSurge?.active;
  const coldRegions = new Set(cold ? (d.coldSurge!.regions ?? []) : []);
  const chips = d.points.filter((p) => p.windDir != null).map((p) => {
    const w = p.windSpeed != null ? ` ${p.windSpeed} กม./ชม.` : "";
    const t = p.temp != null ? ` · ${p.temp}°C` : "";
    return `<div class="mon-chip"><span class="mon-chip-rg">${esc(p.name)}</span>`
      + `<span class="mon-chip-wx">ลมจากทิศ${esc(p.windFromTh ?? "-")}${w}${t}</span></div>`;
  }).join("");
  const surge = cold
    ? `<div class="mon-surge">❄️ ${esc(d.coldSurge!.note)}</div>` : "";
  const upd = d.updatedAt ? ` · อัปเดต ${esc(thTime(d.updatedAt))}` : "";
  return {
    points: d.points, coldRegions,
    html: `<div class="mon-head-row"><span class="mon-head ${cold ? "cold" : ""}">${esc(d.headline)}</span></div>`
      + `<div class="mon-detail muted small">${esc(d.detail)}</div>`
      + surge
      + `<div class="mon-chips">${chips}</div>`
      + `<div class="muted small mon-src">ที่มา: ${esc(d.source)}${upd}</div>`,
  };
}

function ensoSection(e: EnsoData | null): string {
  const head = `<div class="mon-sub-head">🌊 บริบทฤดูกาล: เอลนีโญ / ลานีญา</div>`;
  if (!e || e.state == null) {
    return head + `<div class="mon-nodata muted">ยังไม่มีข้อมูล ENSO ในขณะนี้</div>`;
  }
  const cls = e.state === "el-nino" ? "en-warm" : e.state === "la-nina" ? "en-cool" : "en-neutral";
  const oni = e.oni != null ? `${e.oni > 0 ? "+" : ""}${e.oni}` : "–";
  const strength = e.strength ? ` · ระดับ${esc(e.strength)}` : "";
  const trend = e.trend?.length
    ? `<div class="muted small mon-trend">ค่า ONI ล่าสุด: ${e.trend.map((t) => `${esc(t.season)} ${t.oni > 0 ? "+" : ""}${t.oni}`).join(" · ")}</div>`
    : "";
  const upd = e.updatedAt ? ` · ดึงเมื่อ ${esc(thDate(e.updatedAt))}` : "";
  return head
    + `<div class="mon-enso"><span class="en-pill ${cls}">${esc(e.label)}</span>`
    + `<span class="muted small">ONI ${esc(oni)} °C${strength}</span></div>`
    + `<div class="mon-detail muted small">${esc(e.context)}</div>`
    + trend
    + `<div class="mon-note small">${esc(e.note)}</div>`
    + `<div class="muted small mon-src">ที่มา: ${esc(e.source)}${e.season ? ` · ช่วงข้อมูล ${esc(e.season)}` : ""}${upd}</div>`;
}

let open = false;
let stopAnim: (() => void) | null = null;

/** Toggle the panel. Returns the new open state so the caller can set aria-pressed. */
export async function toggleMonsoonPanel(): Promise<boolean> {
  const existing = document.querySelector(".mon-panel");
  if (existing || open) { closeMonsoonPanel(); return false; }
  open = true;

  const box = document.createElement("div");
  box.className = "mon-panel"; box.setAttribute("role", "dialog"); box.setAttribute("aria-label", "ลมมรสุมและมวลอากาศ");
  box.innerHTML = `<div class="mon-head-bar"><b>🌬 ลมมรสุม & มวลอากาศ</b><button class="mon-x" aria-label="ปิด">✕</button></div>
    <div class="mon-anim-wrap"><canvas class="mon-canvas" aria-hidden="true"></canvas>
      <div class="mon-anim-cap muted small">ลูกศร = ทิศ/ความเร็วลมจริงราย 6 ภูมิภาค (ที่มา+เวลาด้านล่าง) · เส้นขอบประเทศเป็นภาพประกอบโดยประมาณ</div></div>
    <div class="mon-loading muted small">กำลังโหลดข้อมูลลม/อุณหภูมิ…</div>
    <div class="mon-body" hidden></div>`;
  box.querySelector(".mon-x")?.addEventListener("click", () => closeMonsoonPanel());
  document.body.append(box);

  const [m, e] = await Promise.all([loadMonsoon(), loadEnso()]);
  if (!open || !document.body.contains(box)) return false; // closed while loading

  const sec = monsoonSection(m);
  const bodyEl = box.querySelector<HTMLElement>(".mon-body")!;
  box.querySelector(".mon-loading")?.remove();
  bodyEl.hidden = false;
  bodyEl.innerHTML = sec.html + `<div class="mon-divider"></div>` + ensoSection(e);
  bodyEl.append(emergencyCard());

  const canvas = box.querySelector<HTMLCanvasElement>(".mon-canvas");
  if (canvas) { stopAnim?.(); stopAnim = startWindMap(canvas, sec.points, sec.coldRegions); }
  return true;
}

export function closeMonsoonPanel(): void {
  const wasOpen = open;
  open = false;
  stopAnim?.(); stopAnim = null;
  document.querySelector(".mon-panel")?.remove();
  if (wasOpen) window.dispatchEvent(new Event("fs-monsoon-closed"));
}
